/**
 * Mobile admin sub-pages — each one is a small focused screen. All routed under /app/admin/*.
 * Auth: every screen is wrapped by the OperatorAdmin index gate (is_admin check).
 *
 * Sub-pages:
 *   /app/admin/operators       — list, add, rename, set PIN, toggle admin/active
 *   /app/admin/fields          — list per customer, add via AddFieldMap, delete
 *   /app/admin/passing-places  — list, delete (add happens on the silage tracker itself)
 *   /app/admin/diary           — list everyone's recent diary entries
 *   /app/admin/machines        — list known machines (FieldOps + local manual)
 *   /app/admin/integrations    — Xero + FieldOps connection status
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { opApi } from "@/lib/operator";
import OperatorHeader from "@/components/OperatorHeader";
import AddFieldMap from "@/components/AddFieldMap";
import { ChevronLeft, ChevronRight, Plus, Trash2, Loader2, CheckCircle2, AlertCircle, X, Cloud, MapPin } from "lucide-react";

function Shell({ title, back = "/app/admin", children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <OperatorHeader back={back} title={title} />
      <div className="px-4 sm:px-6 py-4 max-w-3xl mx-auto space-y-3">
        {children}
      </div>
    </div>
  );
}


// ---------- Operators ----------
export function AdminOperators() {
  const [ops, setOps] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);   // currently-edited operator (access modal)
  const [newName, setNewName] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try { const r = await opApi.get("/app/admin/operators"); setOps(r.data || []); }
    catch { setOps([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setErr(null); setBusy(true);
    try {
      await opApi.post("/app/admin/operators", { name: newName.trim(), pin: newPin });
      setShowAdd(false); setNewName(""); setNewPin(""); load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const toggle = async (op, field) => {
    try { await opApi.patch(`/app/admin/operators/${op.id}`, { [field]: !op[field] }); load(); }
    catch (e) { window.alert(e.response?.data?.detail || e.message); }
  };

  const resetPin = async (op) => {
    const pin = window.prompt(`New PIN for ${op.name} (4-6 digits)`, "");
    if (!pin) return;
    try { await opApi.patch(`/app/admin/operators/${op.id}`, { pin }); window.alert("PIN updated"); }
    catch (e) { window.alert(e.response?.data?.detail || e.message); }
  };

  // Pretty "expires in N days" badge — null/blank = "no expiry"
  const expiryBadge = (op) => {
    if (!op.expires_at) return null;
    const exp = new Date(op.expires_at);
    const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
    if (days < 0) return <span className="bg-rose-100 text-rose-700 font-bold px-1.5 rounded">EXPIRED</span>;
    if (days <= 7) return <span className="bg-amber-100 text-amber-700 font-bold px-1.5 rounded">EXPIRES IN {days}d</span>;
    return <span className="bg-slate-100 text-slate-600 px-1.5 rounded">expires {exp.toISOString().slice(0, 10)}</span>;
  };

  return (
    <Shell title="Operators">
      <button onClick={() => setShowAdd(true)} data-testid="op-add"
              className="w-full bg-[#c41e3a] hover:bg-[#d92a47] text-white font-bold rounded-xl py-3 flex items-center justify-center gap-2 shadow">
        <Plus size={16} /> Add operator
      </button>

      {ops === null ? <div className="text-center py-6 text-slate-400"><Loader2 className="inline animate-spin" /></div> : (
        <div className="space-y-2">
          {ops.map(op => (
            <div key={op.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`op-row-${op.id}`}>
              <div className="w-10 h-10 rounded-full bg-[#1a1a5c] text-white grid place-items-center font-black text-sm">{op.initials}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1a1a5c]">{op.name}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                  {op.is_admin && <span className="bg-amber-100 text-amber-700 font-bold px-1.5 rounded">ADMIN</span>}
                  {!op.active && <span className="bg-rose-100 text-rose-700 font-bold px-1.5 rounded">DISABLED</span>}
                  {op.silage_only && <span className="bg-sky-100 text-sky-700 font-bold px-1.5 rounded">SILAGE ONLY</span>}
                  {expiryBadge(op)}
                </div>
              </div>
              <button onClick={() => resetPin(op)} className="text-xs text-[#1a1a5c] hover:underline px-2 py-1">PIN</button>
              <button onClick={() => setEditing(op)} data-testid={`op-edit-${op.id}`}
                      className="text-xs text-[#1a1a5c] hover:underline px-2 py-1 font-bold">Edit</button>
              <button onClick={() => toggle(op, "active")}   className="text-xs text-[#c41e3a] hover:underline px-2 py-1">{op.active ? "Disable" : "Enable"}</button>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-[5500] bg-slate-900/85 grid place-items-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-black text-xl text-[#1a1a5c]">New operator</h2>
              <button onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>
            {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded px-3 py-2 mb-2"><AlertCircle size={12} className="inline mr-1" />{err}</div>}
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name" autoFocus
                   className="w-full mb-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <input value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   placeholder="PIN (4-6 digits)" inputMode="numeric"
                   className="w-full mb-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <button onClick={create} disabled={busy || !newName || !newPin}
                    className="w-full bg-[#c41e3a] hover:bg-[#d92a47] disabled:opacity-40 text-white font-bold rounded-lg py-2.5 text-sm">
              {busy ? <><Loader2 size={14} className="inline animate-spin mr-1" />Creating…</> : "Create"}
            </button>
            <p className="text-[10px] text-slate-400 text-center mt-2">
              You can set access duration + silage-tracker-only from the operator list after creating.
            </p>
          </div>
        </div>
      )}

      {editing && (
        <EditOperatorModal op={editing} onClose={() => setEditing(null)}
                           onSaved={() => { setEditing(null); load(); }} />
      )}
    </Shell>
  );
}


// Per-operator access modal — admin toggles is_admin + silage_only + sets expiry date.
function EditOperatorModal({ op, onClose, onSaved }) {
  const [isAdmin, setIsAdmin] = useState(!!op.is_admin);
  const [silageOnly, setSilageOnly] = useState(!!op.silage_only);
  // Date input expects YYYY-MM-DD; if the backend returned an ISO datetime, trim to date part.
  const initialDate = op.expires_at ? String(op.expires_at).slice(0, 10) : "";
  const [expiresAt, setExpiresAt] = useState(initialDate);
  const [presetBusy, setPresetBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const todayPlus = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const applyPreset = (days) => {
    setPresetBusy(true);
    setExpiresAt(days == null ? "" : todayPlus(days));
    setTimeout(() => setPresetBusy(false), 200);
  };

  const save = async () => {
    setErr(null); setBusy(true);
    try {
      await opApi.patch(`/app/admin/operators/${op.id}`, {
        is_admin: isAdmin,
        silage_only: silageOnly,
        expires_at: expiresAt || "",   // "" clears expiry on the backend
      });
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[5500] bg-slate-900/85 grid place-items-center px-4" data-testid="op-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black text-xl text-[#1a1a5c]">{op.name}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded px-3 py-2 mb-3"><AlertCircle size={12} className="inline mr-1" />{err}</div>}

        <label className="flex items-start gap-3 mb-3 cursor-pointer">
          <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)}
                 data-testid="op-is-admin"
                 className="mt-0.5 w-5 h-5 accent-[#c41e3a]" />
          <div>
            <div className="font-bold text-[#1a1a5c] text-sm">Admin</div>
            <div className="text-[11px] text-slate-500">Can manage operators, fields and machines from the mobile admin.</div>
          </div>
        </label>

        <label className="flex items-start gap-3 mb-4 cursor-pointer">
          <input type="checkbox" checked={silageOnly} onChange={e => setSilageOnly(e.target.checked)}
                 data-testid="op-silage-only"
                 className="mt-0.5 w-5 h-5 accent-[#c41e3a]" />
          <div>
            <div className="font-bold text-[#1a1a5c] text-sm">Silage tracker only</div>
            <div className="text-[11px] text-slate-500">After login, skip the operator home and open the silage tracker straight away. They won&apos;t see diary, jobs or admin.</div>
          </div>
        </label>

        <div className="mb-1.5">
          <div className="font-bold text-[#1a1a5c] text-sm">Access expires</div>
          <div className="text-[11px] text-slate-500 mb-2">Leave blank for permanent access.</div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 mb-2">
          {[
            { label: "1 day",   days: 1 },
            { label: "1 week",  days: 7 },
            { label: "1 month", days: 30 },
            { label: "Forever", days: null },
          ].map(p => (
            <button key={p.label} onClick={() => applyPreset(p.days)} disabled={presetBusy}
                    data-testid={`op-expiry-preset-${p.label.replace(/\s+/g, "")}`}
                    className="text-[11px] font-bold py-2 rounded-lg border border-slate-200 hover:border-[#1a1a5c] hover:bg-[#1a1a5c] hover:text-white text-[#1a1a5c] transition">
              {p.label}
            </button>
          ))}
        </div>
        <input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
               data-testid="op-expiry-date"
               className="w-full mb-4 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a5c]" />

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg py-2.5 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="op-edit-save"
                  className="flex-1 bg-[#c41e3a] hover:bg-[#d92a47] disabled:opacity-40 text-white font-bold rounded-lg py-2.5 text-sm">
            {busy ? <><Loader2 size={14} className="inline animate-spin mr-1" />Saving…</> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------- Fields ----------
export function AdminFields() {
  const [clients, setClients] = useState(null);
  const [pickedClient, setPickedClient] = useState(null);
  const [fields, setFields] = useState(null);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    opApi.get("/app/tracker/configured-clients").then(r => setClients(r.data || [])).catch(() => setClients([]));
  }, []);

  const loadFields = async (c) => {
    setPickedClient(c); setFields(null);
    const reqs = [opApi.get(`/app/tracker/fields?client_id=${encodeURIComponent(c.id)}`)];
    if (c.source === "fieldops" || (c.id || "").startsWith("fieldops:")) {
      reqs.push(opApi.get(`/app/tracker/fieldops-fields?grower=${encodeURIComponent(c.name)}`));
    }
    try {
      const res = await Promise.all(reqs);
      const seen = new Set();
      const merged = res.flatMap(r => r.data || []).filter(f => seen.has(f.id) ? false : (seen.add(f.id), true));
      setFields(merged);
    } catch { setFields([]); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete field "${f.name}"?`)) return;
    if ((f.id || "").startsWith("fieldops:")) {
      window.alert("FieldOps fields can't be deleted from here — manage them in CNH FieldOps."); return;
    }
    try { await opApi.delete(`/app/tracker/fields/${f.id}`); loadFields(pickedClient); }
    catch (e) { window.alert(e.response?.data?.detail || e.message); }
  };

  if (!pickedClient) {
    return (
      <Shell title="Fields">
        <div className="text-xs text-slate-500 mb-2">Pick a customer to see their fields:</div>
        {clients === null ? <div className="text-center py-6"><Loader2 className="inline animate-spin text-slate-400" /></div> : (
          <div className="space-y-2">
            {clients.map(c => (
              <button key={c.id} onClick={() => loadFields(c)} data-testid={`adm-client-${c.id}`}
                      className="w-full text-left bg-white hover:bg-amber-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#1a1a5c]">{c.name}</div>
                  <div className="text-[10px] text-slate-500 flex gap-2 items-center">
                    <span>{c.field_count} field{c.field_count === 1 ? "" : "s"}</span>
                    {c.source === "fieldops" && <span className="bg-emerald-100 text-emerald-700 font-bold px-1.5 rounded">FieldOps</span>}
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-400" />
              </button>
            ))}
            {clients.length === 0 && <div className="text-center text-slate-500 py-6">No customers yet. Add a field below to set up the first one.</div>}
          </div>
        )}
        <button onClick={() => setShowMap(true)} data-testid="fields-add-blank"
                className="w-full bg-[#c41e3a] hover:bg-[#d92a47] text-white font-bold rounded-xl py-3 mt-2">
          <Plus size={14} className="inline mr-1" /> Add field for a new customer
        </button>
        {showMap && (
          <AddFieldMap client={null} onCancel={() => setShowMap(false)}
                        onSaved={() => { setShowMap(false); opApi.get("/app/tracker/configured-clients").then(r => setClients(r.data || [])); }} />
        )}
      </Shell>
    );
  }

  return (
    <Shell title={pickedClient.name}>
      <button onClick={() => { setPickedClient(null); setFields(null); }}
              className="text-xs text-[#1a1a5c] font-bold flex items-center gap-1 mb-1">
        <ChevronLeft size={14} /> All customers
      </button>
      <button onClick={() => setShowMap(true)} data-testid="fields-add"
              className="w-full bg-[#c41e3a] hover:bg-[#d92a47] text-white font-bold rounded-xl py-3 flex items-center justify-center gap-2">
        <Plus size={16} /> Add field for {pickedClient.name}
      </button>
      {fields === null ? <div className="text-center py-6"><Loader2 className="inline animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {fields.map(f => (
            <div key={f.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
              <MapPin size={16} className="text-[#c41e3a]" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1a1a5c] truncate">{f.name}</div>
                <div className="text-[10px] text-slate-500">
                  {f.lat.toFixed(4)}, {f.lon.toFixed(4)}
                  {f.polygon_source && ` · ${f.polygon_source}`}
                  {(f.id || "").startsWith("fieldops:") && <span className="ml-2 bg-emerald-100 text-emerald-700 font-bold px-1.5 rounded">FieldOps</span>}
                </div>
              </div>
              <button onClick={() => remove(f)} className="p-2 text-slate-400 hover:text-rose-500"><Trash2 size={14} /></button>
            </div>
          ))}
          {fields.length === 0 && <div className="text-center text-slate-500 py-6">No fields yet. Tap &quot;Add field&quot; above.</div>}
        </div>
      )}
      {showMap && (
        <AddFieldMap client={pickedClient} onCancel={() => setShowMap(false)}
                      onSaved={() => { setShowMap(false); loadFields(pickedClient); }} />
      )}
    </Shell>
  );
}


// ---------- Passing places ----------
export function AdminPassingPlaces() {
  const [places, setPlaces] = useState(null);
  const load = async () => {
    try { const r = await opApi.get("/app/tracker/passing-places"); setPlaces(r.data || []); }
    catch { setPlaces([]); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (p) => {
    if (!window.confirm(`Delete passing place "${p.name}"?`)) return;
    try { await opApi.delete(`/app/tracker/passing-places/${p.id}`); load(); }
    catch (e) { window.alert(e.response?.data?.detail || e.message); }
  };
  return (
    <Shell title="Passing places">
      <div className="text-xs text-slate-500 mb-2">Add new ones from the Silage Tracker map (tap &quot;+ Add passing place&quot; there).</div>
      {places === null ? <div className="text-center py-6"><Loader2 className="inline animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {places.map(p => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
              <MapPin size={16} className="text-amber-500" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1a1a5c]">{p.name}</div>
                <div className="text-[10px] text-slate-500">
                  {p.lat.toFixed(4)}, {p.lon.toFixed(4)}{p.client_name && ` · ${p.client_name}`}
                </div>
              </div>
              <button onClick={() => remove(p)} className="p-2 text-slate-400 hover:text-rose-500"><Trash2 size={14} /></button>
            </div>
          ))}
          {places.length === 0 && <div className="text-center text-slate-500 py-6">No passing places yet.</div>}
        </div>
      )}
    </Shell>
  );
}


// ---------- Diary review ----------
export function AdminDiary() {
  const [entries, setEntries] = useState(null);
  useEffect(() => {
    opApi.get("/app/admin/diary?limit=50").then(r => setEntries(r.data?.entries || [])).catch(() => setEntries([]));
  }, []);
  return (
    <Shell title="Diary review">
      {entries === null ? <div className="text-center py-6"><Loader2 className="inline animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {entries.map(e => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-bold text-[#1a1a5c] text-sm flex-1 truncate">{e.operator_name}{e.task && ` · ${e.task}`}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{e.work_date || e.created_at?.slice(0, 10)}</div>
              </div>
              <div className="text-xs text-slate-600">{e.client_name || "—"}{e.field_name && ` · ${e.field_name}`}</div>
              <div className="text-[11px] text-slate-500 flex gap-3 mt-1">
                {e.machine_label && <span>{e.machine_label}</span>}
                {e.hours != null && <span>{e.hours} h</span>}
                {e.acres != null && <span>{e.acres} ac</span>}
                {e.status && <span className={`font-bold uppercase ${e.status === "invoiced" ? "text-emerald-600" : e.status === "submitted" ? "text-amber-600" : "text-slate-500"}`}>{e.status}</span>}
              </div>
            </div>
          ))}
          {entries.length === 0 && <div className="text-center text-slate-500 py-6">No diary entries yet.</div>}
        </div>
      )}
      <div className="text-[11px] text-slate-400 text-center mt-2">
        For deeper edits / invoice number setting, use the desktop <a href="/admin/diary" className="underline">/admin/diary</a>.
      </div>
    </Shell>
  );
}


// ---------- Machines ----------
export function AdminMachines() {
  const [machines, setMachines] = useState(null);
  useEffect(() => {
    opApi.get("/app/machines").then(r => setMachines(r.data || [])).catch(() => setMachines([]));
  }, []);
  return (
    <Shell title="Machines">
      {machines === null ? <div className="text-center py-6"><Loader2 className="inline animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {machines.map(m => (
            <div key={m.id || m.vin} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#1a1a5c] text-white grid place-items-center font-black text-xs">{(m.label || m.name || "?").slice(0, 2).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1a1a5c] truncate">{m.label || m.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{m.vin || m.serial || ""}</div>
              </div>
            </div>
          ))}
          {machines.length === 0 && <div className="text-center text-slate-500 py-6">No machines yet. Add via desktop <a href="/admin/machines" className="underline">/admin/machines</a>.</div>}
        </div>
      )}
    </Shell>
  );
}


// ---------- Integrations ----------
function IntegrationCard({ name, st, slug, desktopHref }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await opApi.get(`/app/integrations/${slug}/login`);
      // Full-window redirect so the OAuth callback returns to this tab and updates the cookie/session
      window.location.href = r.data.authorize_url;
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <Cloud size={20} className="text-[#1a1a5c]" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[#1a1a5c]">{name}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1">
          {st === null ? <Loader2 className="animate-spin inline" size={12} /> :
            st?.connected ? <><CheckCircle2 size={12} className="text-emerald-500" /> Connected{st?.tenant_name ? ` · ${st.tenant_name}` : ""}{st?.user_email ? ` · ${st.user_email}` : ""}</> :
            <><AlertCircle size={12} className="text-rose-500" /> Not connected</>}
        </div>
      </div>
      <button onClick={connect} disabled={busy} data-testid={`int-connect-${slug}`}
              className={`text-xs font-bold rounded-lg px-3 py-2 shrink-0 ${st?.connected
                ? "bg-amber-100 hover:bg-amber-200 text-[#1a1a5c]" : "bg-emerald-500 hover:bg-emerald-600 text-white"}`}>
        {busy ? <Loader2 className="animate-spin inline" size={12} /> : (st?.connected ? "Re-auth" : "Connect")}
      </button>
      <a href={desktopHref} className="text-[11px] text-slate-400 hover:text-slate-600 shrink-0">Desktop</a>
    </div>
  );
}

export function AdminIntegrations() {
  const [xero, setXero]       = useState(null);
  const [fieldops, setFOps]   = useState(null);
  useEffect(() => {
    opApi.get("/app/integrations/status").then(r => { setXero(r.data?.xero); setFOps(r.data?.fieldops); })
                                          .catch(() => { setXero({ connected: false }); setFOps({ connected: false }); });
  }, []);
  return (
    <Shell title="Integrations">
      <IntegrationCard name="Xero (customers + invoicing)"      st={xero}     slug="xero" desktopHref="/admin/xero" />
      <IntegrationCard name="CNH FieldOps (machines + fields)"  st={fieldops} slug="cnh"  desktopHref="/admin/fleet" />
      <div className="text-[11px] text-slate-400 text-center pt-2">
        Tapping <strong>Connect</strong> or <strong>Re-auth</strong> opens the provider&apos;s login page;
        you&apos;ll be returned here once finished.
      </div>
    </Shell>
  );
}
